/**
 * ChatComposer — message input for the chat panel.
 *
 * Auto-expanding textarea with send button and cancel-stream button.
 * Handles Enter to send, Shift+Enter for newline.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeClasses } from '../../providers/ThemeProvider';
import { cx } from '../../styles';

interface ChatComposerProps {
  onSend: (text: string) => void;
  onCancel?: () => void;
  isStreaming: boolean;
  disabled?: boolean;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  onSend,
  onCancel,
  isStreaming,
  disabled,
}) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize the textarea
  const adjustHeight = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  const handleSubmit = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const text = ta.value.trim();
    if (!text || isStreaming || disabled) return;
    onSend(text);
    ta.value = '';
    adjustHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={cx('flex items-end gap-2 px-3 py-2.5 border-t', tc.borderPrimary, tc.bgSurface)}>
      <textarea
        ref={textareaRef}
        placeholder={t('cockpit.placeholder', 'Ask about your production data...')}
        onKeyDown={handleKeyDown}
        onInput={adjustHeight}
        disabled={disabled}
        rows={1}
        className={cx(
          'flex-1 resize-none bg-transparent outline-none text-sm leading-relaxed py-1.5',
          'min-h-[36px] max-h-[120px]',
          tc.textPrimary,
          'placeholder:text-gray-400 dark:placeholder:text-slate-500',
          'disabled:opacity-50',
        )}
      />
      {isStreaming ? (
        <button
          onClick={onCancel}
          className={cx(
            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
            'bg-red-500 text-white',
            'hover:bg-red-600 transition-colors',
          )}
          title={t('cockpit.stopGenerating', 'Stop generating')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
            <rect x="3.5" y="3.5" width="9" height="9" rx="1" />
          </svg>
        </button>
      ) : (
        <button
          onClick={handleSubmit}
          disabled={disabled}
          className={cx(
            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
            'bg-[#025a94] text-white',
            'hover:bg-[#037ac7] transition-colors',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
          title={t('cockpit.send', 'Send')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
            <path d="M2.87 2.298a.75.75 0 00-.812.81l.959 5.078a.75.75 0 00.636.579l4.095.574a.25.25 0 010 .494l-4.095.574a.75.75 0 00-.636.58l-.959 5.077a.75.75 0 00.812.81l11.505-5.686a.75.75 0 000-1.378L2.87 2.298z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ChatComposer;
